// Solen AR — Halı için "Odanda Gör" deneyimi
// Android: WebXR Hit Test (immersive-ar)
// iOS: Three.js USDZExporter ile client-side .usdz uretimi + AR Quick Look
// Diger: 2D fallback (kamera + halı overlay)
// Ekstra altyapi YOK, her sey browser'da calisir.

import * as THREE from 'https://esm.sh/three@0.160.0';
import { USDZExporter } from 'https://esm.sh/three@0.160.0/examples/jsm/exporters/USDZExporter.js';

const SolenAR = {
  inited: false,

  init() {
    if (this.inited) return;
    this.inited = true;
    document.querySelectorAll('[data-solen-ar]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        this.launch(btn);
      });
    });
    // Variant degisince butonun image/varyant data'sini guncelle
    this.observeVariantChanges();
  },

  observeVariantChanges() {
    document.addEventListener('change', e => {
      if (!e.target.matches('input[name^="options"], select[name^="options"]')) return;
      // Shopify variant change event'ini dinle, ya da formdan oku
      setTimeout(() => this.refreshButtonsFromForm(), 100);
    });
  },

  refreshButtonsFromForm() {
    // Sayfadaki secili variant ID'yi bul ve buton data'sini guncelle
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const variantId = form.querySelector('input[name="id"]')?.value;
    if (!variantId || !window.solenProductVariants) return;
    const variant = window.solenProductVariants.find(v => String(v.id) === String(variantId));
    if (!variant) return;
    document.querySelectorAll('[data-solen-ar]').forEach(btn => {
      btn.dataset.variant = variant.title || '';
      if (variant.featured_image?.src) btn.dataset.image = variant.featured_image.src;
    });
  },

  parseSize(str) {
    // "200 x 300", "200x300 cm", "80x150" → metre cinsinden
    if (!str) return { w: 1.6, h: 2.3 };
    const m = str.match(/(\d{2,3})\s*[xX×]\s*(\d{2,3})/);
    if (!m) return { w: 1.6, h: 2.3 };
    const a = parseInt(m[1], 10) / 100;
    const b = parseInt(m[2], 10) / 100;
    // Halı genelde uzun kenar Y ekseninde
    return { w: Math.min(a, b), h: Math.max(a, b) };
  },

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  },

  async launch(btn) {
    const imageUrl = btn.dataset.image;
    const variantTitle = btn.dataset.variant || '';
    const productTitle = btn.dataset.title || 'Hali';

    if (!imageUrl) {
      alert('Halı görseli bulunamadı.');
      return;
    }

    const { w, h } = this.parseSize(variantTitle);
    this.showLoading('AR hazırlanıyor…');

    try {
      if (this.isIOS()) {
        await this.iosQuickLook(imageUrl, w, h, productTitle);
      } else if (navigator.xr && (await navigator.xr.isSessionSupported('immersive-ar').catch(() => false))) {
        this.hideLoading();
        await this.androidWebXR(imageUrl, w, h);
      } else {
        this.hideLoading();
        this.fallback2D(imageUrl, productTitle, w, h);
      }
    } catch (err) {
      console.error('Solen AR hata:', err);
      this.hideLoading();
      this.fallback2D(imageUrl, productTitle, w, h);
    }
  },

  // ==================== iOS: USDZ + AR Quick Look ====================
  async iosQuickLook(imageUrl, w, h, title) {
    const scene = new THREE.Scene();
    const texture = await new THREE.TextureLoader().setCrossOrigin('anonymous').loadAsync(imageUrl);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = false;

    const geometry = new THREE.PlaneGeometry(w, h);
    geometry.rotateX(-Math.PI / 2); // geometry seviyesinde yatır (USDZ export icin)
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      side: THREE.DoubleSide,
      roughness: 0.8,
      metalness: 0.0
    });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const exporter = new USDZExporter();
    const arrayBuffer = await exporter.parse(scene);
    const blob = new Blob([arrayBuffer], { type: 'model/vnd.usdz+zip' });
    const url = URL.createObjectURL(blob);

    // AR Quick Look icin <a rel="ar"> + ic <img>
    const a = document.createElement('a');
    a.rel = 'ar';
    a.href = url;
    a.setAttribute('download', `${title}.usdz`);
    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.display = 'none';
    a.appendChild(img);
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
      this.hideLoading();
    }, 1000);
  },

  // ==================== Android: WebXR Hit Test ====================
  async androidWebXR(imageUrl, w, h) {
    const overlay = document.createElement('div');
    overlay.id = 'solen-ar-overlay';
    overlay.innerHTML = `
      <div class="solen-ar-hint">Telefonu zemine doğrult, halıyı yerleştirmek için ekrana dokun</div>
      <button class="solen-ar-close" type="button" aria-label="Kapat">✕</button>
    `;
    document.body.appendChild(overlay);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera();

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.xr.enabled = true;
    renderer.domElement.id = 'solen-ar-canvas';
    document.body.appendChild(renderer.domElement);

    const light = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1.2);
    scene.add(light);
    const dir = new THREE.DirectionalLight(0xffffff, 0.6);
    dir.position.set(1, 2, 1);
    scene.add(dir);

    const texture = await new THREE.TextureLoader().setCrossOrigin('anonymous').loadAsync(imageUrl);
    texture.colorSpace = THREE.SRGBColorSpace;
    const rugGeo = new THREE.PlaneGeometry(w, h);
    const rugMat = new THREE.MeshStandardMaterial({
      map: texture,
      side: THREE.DoubleSide,
      roughness: 0.85
    });
    const rug = new THREE.Mesh(rugGeo, rugMat);
    rug.rotation.x = -Math.PI / 2;
    rug.visible = false;
    scene.add(rug);

    const reticle = new THREE.Mesh(
      new THREE.RingGeometry(0.10, 0.12, 32).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0xc25b41 })
    );
    reticle.matrixAutoUpdate = false;
    reticle.visible = false;
    scene.add(reticle);

    let session;
    try {
      session = await navigator.xr.requestSession('immersive-ar', {
        requiredFeatures: ['hit-test'],
        optionalFeatures: ['dom-overlay'],
        domOverlay: { root: overlay }
      });
    } catch (err) {
      renderer.domElement.remove();
      overlay.remove();
      throw err;
    }

    await renderer.xr.setSession(session);
    const viewerSpace = await session.requestReferenceSpace('viewer');
    const refSpace = await session.requestReferenceSpace('local');
    const hitTestSource = await session.requestHitTestSource({ space: viewerSpace });

    const controller = renderer.xr.getController(0);
    controller.addEventListener('select', () => {
      if (reticle.visible) {
        rug.position.setFromMatrixPosition(reticle.matrix);
        rug.visible = true;
      }
    });
    scene.add(controller);

    overlay.querySelector('.solen-ar-close').onclick = () => session.end();

    session.addEventListener('end', () => {
      renderer.setAnimationLoop(null);
      renderer.domElement.remove();
      overlay.remove();
    });

    renderer.setAnimationLoop((_, frame) => {
      if (frame) {
        const hits = frame.getHitTestResults(hitTestSource);
        if (hits.length) {
          const pose = hits[0].getPose(refSpace);
          reticle.visible = true;
          reticle.matrix.fromArray(pose.transform.matrix);
        } else {
          reticle.visible = false;
        }
      }
      renderer.render(scene, camera);
    });
  },

  // ==================== Fallback: 2D onizleme ====================
  fallback2D(imageUrl, title, w, h) {
    const modal = document.createElement('div');
    modal.className = 'solen-ar-fallback';
    modal.innerHTML = `
      <div class="solen-ar-fallback__inner">
        <button class="solen-ar-close" type="button" aria-label="Kapat">✕</button>
        <h3>${title}</h3>
        <p>Bu cihazda tam AR desteklenmiyor. Halının gerçek boyutu: ${(w * 100).toFixed(0)} x ${(h * 100).toFixed(0)} cm</p>
        <img src="${imageUrl}" alt="${title}" />
        <p class="solen-ar-tip">İpucu: Tam AR deneyimi için Android'de Chrome veya iOS Safari kullanın.</p>
      </div>
    `;
    document.body.appendChild(modal);
    modal.querySelector('.solen-ar-close').onclick = () => modal.remove();
    modal.addEventListener('click', e => {
      if (e.target === modal) modal.remove();
    });
  },

  // ==================== UI helpers ====================
  showLoading(text) {
    let m = document.getElementById('solen-ar-loading');
    if (!m) {
      m = document.createElement('div');
      m.id = 'solen-ar-loading';
      m.innerHTML = `<div class="solen-ar-spinner"></div><p></p>`;
      document.body.appendChild(m);
    }
    m.querySelector('p').textContent = text;
    m.style.display = 'flex';
  },

  hideLoading() {
    const m = document.getElementById('solen-ar-loading');
    if (m) m.style.display = 'none';
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => SolenAR.init());
} else {
  SolenAR.init();
}

window.SolenAR = SolenAR;
